import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ClosedWonMonth, ClosedWonTotals } from '@/models/salesAnalytics'

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })

interface Props {
  title: string
  description: string
  months: ClosedWonMonth[]
  totals: ClosedWonTotals
}

export function ClosedWonCard({ title, description, months, totals }: Props) {
  const peak = Math.max(...months.map((m) => m.premium), 1)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-neutral-500">{description}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex gap-6">
          <div>
            <div className="text-2xl tabular-nums">{money(totals.premium)}</div>
            <div className="text-xs text-neutral-500">Total premium</div>
          </div>
          <div>
            <div className="text-2xl tabular-nums">{totals.accounts}</div>
            <div className="text-xs text-neutral-500">Accounts won</div>
          </div>
          <div>
            <div className="text-2xl tabular-nums">{totals.policies}</div>
            <div className="text-xs text-neutral-500">Policies</div>
          </div>
        </div>
        {totals.undated > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {totals.undated} {totals.undated === 1 ? 'policy has' : 'policies have'} no date on this axis and{' '}
            {totals.undated === 1 ? 'is' : 'are'} not counted here.
          </p>
        )}
        {months.length === 0 && <div className="text-sm text-neutral-500">Nothing in range.</div>}
        {months.map((month) => (
          <div key={month.month} className="grid grid-cols-[5rem_1fr_auto] items-center gap-3">
            <div className="text-sm text-neutral-500">{monthLabel(month.month)}</div>
            <div className="h-6 overflow-hidden rounded bg-neutral-100 dark:bg-neutral-800">
              <div
                className="h-full rounded bg-emerald-500"
                style={{ width: `${(month.premium / peak) * 100}%` }}
              />
            </div>
            <div className="text-sm whitespace-nowrap tabular-nums">
              {money(month.premium)}
              <span className="text-neutral-500">
                {' · '}
                {month.accounts} {month.accounts === 1 ? 'account' : 'accounts'}
                {month.policies === month.accounts ? '' : ` / ${month.policies} policies`}
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
